/* eslint-disable no-nested-ternary */
import React, {
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  ThemeProvider,
  Typography,
  useTheme,
} from '@mui/material';
import { Box } from '@mui/system';
import BookmarkBorderOutlinedIcon from '@mui/icons-material/BookmarkBorderOutlined';
import { Trans, useTranslation } from 'react-i18next';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { useAppSelector } from '../../../store/hooks';
import { useIsDesktop } from '../../../common/hooks/useResponsive';
import { ContentTheme } from './ContentTheme';
import { selectMemberInformation } from '../../../store/slices/onboardingSlice';
import { TopTradeContentCategory } from './TopTradeContentCategory';
import { RewardsContentCategory } from './RewardsContentCategory';
import { VideoContentCategory } from './VideoContentCategory';
import { PodcastContentCategory } from './PodcastContentCategory';
import { Topic1ContentCategory } from './Topic1ContentCategory';
import { Topic2ContentCategory } from './Topic2ContentCategory';
import { Topic3ContentCategory } from './Topic3ContentCategory';
import { Topic4ContentCategory } from './Topic4ContentCategory';
import { Topic5ContentCategory } from './Topic5ContentCategory';
import { FeedInteractionAction } from '../../../common/models/content/Content';
import { useCustomEventTracking } from '../../../common/useCustomEventTracking';
import { useTrackElementVisibilityInViewPort } from '../../../common/hooks/useTrackElementVisibilityInViewPort';
import { LazyRenderContentCategories } from '../../../common/LazyRenderContentCategories';
import { ContentCategory } from '../../../common/enums';
import ContentSearchView from './ContentSearchView';
import { Logger } from '../../../config/logging/logger';
import { FilteredContentCategory } from './FilteredContentCategory';
import { ContentFilters } from './ContentFilters';
import { useSolutionOrg } from '../../../config/useSolutionOrg';
import { ServiceNationSolutionOrgName } from '../../../common/constants';
import OptimusButton from '../../../common/library/Button/Button';
import { RESOURCES_ROUTE, SAVED_ROUTE } from '../../../routes/namedRoutes';
import { TestIds } from '../../../assets/testIds';

export default function ContentHome() {
  const { t } = useTranslation();
  // ContentHome also renders on Home; the Saved link belongs to Resources only
  const isResourcesPage = useLocation().pathname === RESOURCES_ROUTE;
  const theme = useTheme();
  const isDesktop = useIsDesktop();
  const navigate = useNavigate();
  const { company } = useAppSelector(selectMemberInformation);
  const [someContentLoadedInitially, setSomeContentLoadedInitially] = useState(false);
  const { trackFeedInteraction } = useCustomEventTracking();
  const contentFeedEndMessageRef = useRef(null);
  const trackElementVisibility = useTrackElementVisibilityInViewPort();
  const logger = Logger.create('ContentHome');
  // `?filter=<tag>` opens the page on that filter, e.g. the home page's "View More" link.
  const [searchParams, setSearchParams] = useSearchParams();
  const initialFilter = searchParams.get('filter') || undefined;
  const userBusinessTrades = useMemo(
    () => company?.businessTrades?.map((businessTrade) => businessTrade.name).join(',') || '',
    [company?.businessTrades],
  );
  const { solutionOrgName } = useSolutionOrg();

  const filterContents = [
    {
      title: t('home.contentFilters.allResources.title'),
      options: [],
      tag: t('home.contentFilters.allResources.tag'),
    },
    {
      title: t('home.contentFilters.featured.title'),
      options: [],
      tag: t('home.contentFilters.featured.tag'),
    },
    {
      title: t('home.contentFilters.tools.title'),
      options: [],
      tag: t('home.contentFilters.tools.tag'),
    },
    /* {
      title: t('home.contentFilters.new.title'),
      options: [],
      tag: t('home.contentFilters.new.tag'),
    }, */
    {
      title: t('home.contentFilters.marketing.title'),
      options: [
        t('home.contentFilters.marketing.options.leadGeneration'),
        t('home.contentFilters.marketing.options.marketing'),
        t('home.contentFilters.marketing.options.reputationManagement'),
      ],
      tag: t('home.contentFilters.marketing.tag'),
    },
    {
      title: t('home.contentFilters.operations.title'),
      options: [
        t('home.contentFilters.operations.options.technology'),
        t('home.contentFilters.operations.options.operationsManagement'),
        t('home.contentFilters.operations.options.customerService'),
        t('home.contentFilters.operations.options.safety'),
      ],
      tag: t('home.contentFilters.operations.tag'),
    },
    {
      title: t('home.contentFilters.leadership.title'),
      options: [
        t('home.contentFilters.leadership.options.leadership'),
        t('home.contentFilters.leadership.options.networking'),
      ],
      tag: t('home.contentFilters.leadership.tag'),
    },
    {
      title: t('home.contentFilters.sales.title'),
      options: [
        t('home.contentFilters.sales.options.sales'),
        t('home.contentFilters.sales.options.pricing'),
      ],
      tag: t('home.contentFilters.sales.tag'),
    },
    {
      title: t('home.contentFilters.financial.title'),
      options: [
        t('home.contentFilters.financial.options.financesAndTaxes'),
        t('home.contentFilters.financial.options.invoicing'),
        t('home.contentFilters.financial.options.payments'),
      ],
      tag: t('home.contentFilters.financial.tag'),
    },
  ];

  const [selectedFilter, setSelectedFilter] = useState(() => (
    filterContents.some((f) => f.tag === initialFilter) ? initialFilter as string : 'All'
  ));

  // Keep the active tab in the URL so going back from an article restores it.
  const handleFilterChange = (filter: string) => {
    setSelectedFilter(filter);
    setSearchParams((params) => {
      if (filter === 'All') params.delete('filter');
      else params.set('filter', filter);
      return params;
    }, { replace: true });
  };

  // Extract tag constants to ensure localization changes don't break filter logic
  const TOOLS_FILTER_TAG = t('home.contentFilters.tools.tag');
  const FEATURED_FILTER_TAG = t('home.contentFilters.featured.tag');
  const NEW_FILTER_TAG = t('home.contentFilters.new.tag');

  useEffect(() => {
    logger.info(Logger.format('Feed loaded now'));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /**
  * Check if member selected trade falls in top 5 Trades.
  * If yes, then latest in 'Trade' section will be displayed.
  */
  const topTrade = useMemo(() => {
    const topTrades = ['General Contractor', 'Electrician', 'Remodeler', 'Handyman/Handywoman/Handyperson', 'Carpenter'];
    const randomBusinessTradesIndx = Math.floor(
      Math.random() * (company?.businessTrades?.length || 0),
    );
    const tradeName = company?.businessTrades?.[randomBusinessTradesIndx]?.name || '';
    const isTopTrade = topTrades.some((trade) => trade.toLowerCase() === tradeName.toLowerCase());
    return isTopTrade ? tradeName : null;
  }, [company?.businessTrades]);
  const contentFeedSectionOffset = useMemo(() => (topTrade ? 1 : 0), [topTrade]);

  const contentTopics = useMemo(() => ({
    1: {
      categoryId: ContentCategory.TOPIC1,
      name: t('content.categories.marketing'),
    },
    2: {
      categoryId: ContentCategory.TOPIC2,
      name: t('content.categories.pricing'),
    },
    3: {
      categoryId: ContentCategory.TOPIC3,
      name: t('content.categories.leadGeneration'),
    },
    4: {
      categoryId: ContentCategory.TOPIC4,
      name: t('content.categories.hiring'),
    },
    5: {
      categoryId: ContentCategory.TOPIC5,
      name: t('content.categories.training'),
    },
    // Intentionally omitting `t` from deps to avoid unnecessary remounts
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }), []);

  const lazyRenderedContentCategories = useMemo(() => ([
    () => (<Topic1ContentCategory
      categoryId={contentTopics[1].categoryId}
      topicName={contentTopics[1].name}
      setSomeContentLoadedInitially={setSomeContentLoadedInitially}
      contentFeedSection={{
        index: 3 + contentFeedSectionOffset,
        name: contentTopics[1].categoryId,
      }}
    />),
    () => (<Topic2ContentCategory
      categoryId={contentTopics[2].categoryId}
      topicName={contentTopics[2].name}
      setSomeContentLoadedInitially={setSomeContentLoadedInitially}
      contentFeedSection={{
        index: 4 + contentFeedSectionOffset,
        name: contentTopics[2].categoryId,
      }}
    />),
    () => (<Topic3ContentCategory
      categoryId={contentTopics[3].categoryId}
      topicName={contentTopics[3].name}
      setSomeContentLoadedInitially={setSomeContentLoadedInitially}
      contentFeedSection={{
        index: 5 + contentFeedSectionOffset,
        name: contentTopics[3].categoryId,
      }}
    />),
    () => (<Topic4ContentCategory
      categoryId={contentTopics[4].categoryId}
      topicName={contentTopics[4].name}
      setSomeContentLoadedInitially={setSomeContentLoadedInitially}
      contentFeedSection={{
        index: 6 + contentFeedSectionOffset,
        name: contentTopics[4].categoryId,
      }}
    />),
    () => (<Topic5ContentCategory
      categoryId={contentTopics[5].categoryId}
      topicName={contentTopics[5].name}
      setSomeContentLoadedInitially={setSomeContentLoadedInitially}
      contentFeedSection={{
        index: 7 + contentFeedSectionOffset,
        name: contentTopics[5].categoryId,
      }}
    />),
    () => (<VideoContentCategory
      categoryId={ContentCategory.VIDEO}
      keyProp={t('content.categories.video')}
      setSomeContentLoadedInitially={setSomeContentLoadedInitially}
      contentFeedSection={{
        index: 8 + contentFeedSectionOffset,
        name: ContentCategory.VIDEO,
      }}
    />),
    () => (<PodcastContentCategory
      categoryId={ContentCategory.PODCAST}
      keyProp={t('content.categories.podcast')}
      setSomeContentLoadedInitially={setSomeContentLoadedInitially}
      contentFeedSection={{
        index: 9 + contentFeedSectionOffset,
        name: ContentCategory.PODCAST,
      }}
    />),
    // Intentionally omitting `t` from deps to avoid unnecessary remounts
    // eslint-disable-next-line react-hooks/exhaustive-deps
  ]), [setSomeContentLoadedInitially, contentTopics, contentFeedSectionOffset]);

  const computedOptions = useMemo(() => {
    if (selectedFilter === NEW_FILTER_TAG) {
      return `${userBusinessTrades},All Home Services`;
    }

    if (selectedFilter === FEATURED_FILTER_TAG) {
      return 'Featured';
    }

    if (selectedFilter === TOOLS_FILTER_TAG) {
      return 'Performance,Growth,Accelerator';
    }

    const options = filterContents.find((f) => f.tag === selectedFilter)?.options;
    return options?.join(',') || '';
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedFilter, userBusinessTrades, NEW_FILTER_TAG, FEATURED_FILTER_TAG, TOOLS_FILTER_TAG]);

  // Track when first content exists on feed
  useEffect(() => {
    if (someContentLoadedInitially) {
      trackFeedInteraction(
        'View',
        'ContentStart',
        {
          action: FeedInteractionAction.Visit,
          feedVisit: new Date().toISOString(),
        },
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [someContentLoadedInitially]);

  // Track when user reaches the end of the content feed
  useEffect(() => {
    const endMessageElement = contentFeedEndMessageRef.current as unknown as Element;

    const observers = trackElementVisibility([endMessageElement], () => {
      if (contentFeedEndMessageRef.current) {
        trackFeedInteraction(
          'View',
          'ContentEnd',
          {
            action: FeedInteractionAction.ReachEnd,
          },
        );
      }
    });
    // Clean up the observers when the component unmounts
    return () => {
      observers.forEach((observer) => observer.disconnect());
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSubmit = (value: string | undefined) => {
    const params = new URLSearchParams();
    const trimmedValue = value?.trim();
    if (!trimmedValue) return;

    params.set('query', trimmedValue);
    const route = `/search?${params.toString()}`;
    navigate(route);
  };

  return (
    <ThemeProvider theme={ContentTheme}>
      <Box
        id='content-page'
        sx={{
          padding: theme.spacing(4),
          width: '100%',
          margin: '0 auto',
          [theme.breakpoints.down('sm')]: {
            overflowX: 'clip',
            padding: theme.spacing(0),
          },

        }}
      >
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            gap: theme.spacing(2),
            width: '100%',
          }}
        >
          <Box sx={{ flex: 1 }}>
            <ContentSearchView
              onSubmit={handleSubmit}
            />
          </Box>
          {isResourcesPage &&
          <OptimusButton
            data-testid={TestIds.contentFeed.savedBtn}
            customVariant='secondaryGray'
            size='m'
            fullWidth={false}
            // Stretch to the Search field's height so both controls line up
            height='auto'
            // Hug the content so only the size's 10px 14px padding applies (per Figma)
            minWidth='auto'
            leadingIcon={BookmarkBorderOutlinedIcon}
            buttonText={`${t('navigation.saved')}`}
            onClick={() => navigate(SAVED_ROUTE)}
            sx={{ alignSelf: 'stretch', border: `1px solid ${theme.palette.grey[300]}` }}
          />}
        </Box>
        <Box sx={{
          marginTop: theme.spacing(3),
        }}>
          <ContentFilters
            filterContents={filterContents}
            setSelectedFilter={handleFilterChange}
            initialFilter={selectedFilter}
          />
        </Box>
        <>
          {selectedFilter === 'All' ? <Box>
            {topTrade && (
              <Box>
                <TopTradeContentCategory
                  categoryId={ContentCategory.TOP_TRADE}
                  keyProp={t('content.categories.topTrade')}
                  topTrade={topTrade}
                  setSomeContentLoadedInitially={setSomeContentLoadedInitially}
                  contentFeedSection={{
                    index: 1,
                    name: `topTrade: ${topTrade}`,
                  }}
                />
              </Box>
            )}
            {solutionOrgName !== ServiceNationSolutionOrgName &&
              <RewardsContentCategory
                categoryId={ContentCategory.REWARDS}
                keyProp={t('content.categories.rewardsAndMore')}
                setSomeContentLoadedInitially={setSomeContentLoadedInitially}
                contentFeedSection={{
                  index: 2,
                  name: ContentCategory.REWARDS,
                }}
              />
            }
            <LazyRenderContentCategories
              categories={lazyRenderedContentCategories}
              batchSize={2}
              isDesktop={isDesktop}
            />
            <Typography sx={{
              textAlign: 'center',
              fontSize: theme.spacing(2.25),
              marginTop: theme.spacing(4),
              color: theme.palette.grey[500],
              fontWeight: 500,
            }}
            ref={contentFeedEndMessageRef}
            >
              <b>
                <Trans
                  i18nKey="content.feed.endMessage"
                  components={{
                    // eslint-disable-next-line jsx-a11y/anchor-has-content
                    topAnchorLink: <a href="#top-anchor"
                      aria-label={t('content.feed.useSearch') || ''} />,
                  }}
                />
              </b>
            </Typography>
          </Box> :
            <FilteredContentCategory
              key={selectedFilter}
              categoryId={ContentCategory.FILTERED}
              computedOptions={computedOptions}
              selectedFilter={selectedFilter}
              keyProp={t('content.categories.filtered')}
              setSomeContentLoadedInitially={setSomeContentLoadedInitially}
            />
          }
        </>
      </Box>
    </ThemeProvider >);
}
